/* Life Skills v4.0.0 — added preview sections + shared guidance.
   Same white card / gradient-icon style as the 8 existing modal sections. */
import React from 'react';
import { Link } from 'react-router-dom';
import { Bot, ClipboardCheck, MapPin, BookMarked, ArrowRight } from 'lucide-react';
import { Accordion } from '../preview-ui/PreviewPrimitives';

export const Card = ({ id, label, Icon, cardCls, iconCls, children, chip }) => (
  <section className={`bg-white border-2 ${cardCls} rounded-3xl p-5 md:p-7 shadow-sm`} data-testid={`ls-section-${id}`}>
    <div className="flex items-center gap-3 mb-4">
      <span className={`w-10 h-10 rounded-2xl bg-gradient-to-br ${iconCls} text-white flex items-center justify-center shrink-0 shadow-sm`}>
        <Icon size={18} />
      </span>
      <h2 className="text-lg md:text-xl font-extrabold text-gray-900">{label}</h2>
    </div>
    {chip && <span className="inline-block text-[11px] font-bold px-3 py-1 rounded-full border bg-slate-100 text-slate-700 border-slate-200 mb-3">{chip}</span>}
    <div className="text-gray-700 text-base leading-relaxed space-y-3">{children}</div>
  </section>
);

const List = ({ items }) => (
  <ul className="list-disc pl-5 space-y-1.5">{(items || []).map((x, i) => <li key={i}>{x}</li>)}</ul>
);

const Sub = ({ children }) => <p className="text-[11px] uppercase font-bold tracking-[0.18em] text-gray-500 mb-1">{children}</p>;

const Ext = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 text-teal-700 hover:text-teal-900 break-all">{children}</a>
);

export const SkillAddedSections = ({ skill }) => {
  const ai = skill.ai_workforce_reality || {};
  const pc = skill.practical_check || {};
  const ic = skill.india_context || {};
  const ev = skill.evidence || {};
  return (
    <>
      {(ai.tasks_ai_can_assist?.length || ai.what_could_be_automated || ai.limits_and_conditions) && (
        <Card id="ai_workforce_reality" label="How AI and agents change this skill" Icon={Bot}
          cardCls="border-violet-200 bg-violet-50/40" iconCls="from-violet-400 to-purple-500"
          chip="Conditional scenario — not a prediction of job loss or permanent safety">
          {ai.tasks_ai_can_assist?.length > 0 && (<div><Sub>Tasks AI can assist with today</Sub><List items={ai.tasks_ai_can_assist} /></div>)}
          {ai.what_could_be_automated && (<div><Sub>What could become more automated</Sub><p>{ai.what_could_be_automated}</p></div>)}
          {ai.limits_and_conditions && (<div><Sub>Limits and conditions</Sub><p>{ai.limits_and_conditions}</p></div>)}
        </Card>
      )}
      {pc.task && (
        <Card id="practical_check" label="Practical check" Icon={ClipboardCheck}
          cardCls="border-amber-200 bg-amber-50/40" iconCls="from-amber-400 to-orange-500"
          chip="Learning exercise — not a quiz, score or badge requirement">
          <p data-testid="ls-practical-task">{pc.task}</p>
          {pc.success_check && <p><span className="font-semibold">Success check:</span> {pc.success_check}</p>}
        </Card>
      )}
      {(ic.local_checks?.length || ic.who_to_learn_with) && (
        <Card id="india_context" label="Check this where you live" Icon={MapPin}
          cardCls="border-emerald-200 bg-emerald-50/40" iconCls="from-emerald-400 to-teal-500">
          {ic.local_checks?.length > 0 && (<div><Sub>Local checks</Sub><List items={ic.local_checks} /></div>)}
          {ic.who_to_learn_with && (<div><Sub>Who to learn with</Sub><p>{ic.who_to_learn_with}</p></div>)}
        </Card>
      )}
      {(ev.status || ev.sources_resolved?.length > 0 || skill.content_reviewed_on) && (
        <p className="px-2 text-xs text-gray-500 leading-relaxed" data-testid="ls-evidence-note">
          {ev.status ? `${ev.status[0].toUpperCase()}${ev.status.slice(1)}. ` : ''}
          {skill.content_reviewed_on ? `Content reviewed ${skill.content_reviewed_on} — a review date is not individual verification of any employer, course or income. ` : ''}
          {ev.sources_resolved?.length > 0 && (
            <>Sources: {ev.sources_resolved.map((s, i) => <span key={s.id}>{i > 0 && ' · '}<Ext href={s.url}>{s.title}</Ext></span>)}</>
          )}
        </p>
      )}
    </>
  );
};

const AiWorkforce = ({ a, reg }) => (
  <div className="space-y-3 text-[15px]">
    {a.formula && <p className="font-semibold text-gray-900">{a.formula}</p>}
    {a.status && <p className="text-sm text-gray-600">{a.status}</p>}
    {a.reality && <p>{a.reality}</p>}
    {a.decision_rule && <p><span className="font-semibold">Decision rule:</span> {a.decision_rule}</p>}
    {a.agi_asi && <p><span className="font-semibold">AGI / ASI:</span> {a.agi_asi}</p>}
    {a.windows?.length > 0 && (
      <div className="grid sm:grid-cols-3 gap-2" data-testid="ls-guidance-windows">
        {a.windows.map((w) => (
          <div key={w.period} className="rounded-2xl border border-sky-200 bg-sky-50 p-3 text-sm">
            <p className="font-bold text-sky-900">{w.period} · {w.type}</p>
            <p className="mt-1">{w.watch}</p>
          </div>
        ))}
      </div>
    )}
    {a.evidence_anchor?.text && <p className="text-sm">{a.evidence_anchor.text} {(a.evidence_anchor.source_ids || []).map((i) => reg[i] && <Ext key={i} href={reg[i].url}>{reg[i].title}</Ext>)}</p>}
    {a.tool_examples?.length > 0 && (
      <div>
        <Sub>Tool examples — documented scope, not proof of your local job market</Sub>
        <div className="grid sm:grid-cols-2 gap-2" data-testid="ls-guidance-tools">
          {a.tool_examples.map((t) => (
            <div key={t.name} className="rounded-2xl border border-violet-200 bg-violet-50 p-3 text-sm">
              <p className="font-bold text-violet-900">{t.name}</p>
              <p className="mt-1">{t.documented_scope}</p>
              <p className="mt-1"><span className="font-semibold">Learn:</span> {t.learning_focus}</p>
              {(t.source_ids || []).map((i) => reg[i] && <p key={i} className="mt-1 text-xs"><Ext href={reg[i].url}>{reg[i].title}</Ext></p>)}
            </div>
          ))}
        </div>
      </div>
    )}
    {a.practice_rule && <p className="text-sm"><span className="font-semibold">Practice rule:</span> {a.practice_rule}</p>}
  </div>
);

const India = ({ g, reg }) => (
  <div className="space-y-3 text-[15px]">
    {g.scope && <p className="text-sm text-gray-600">{g.scope}</p>}
    {g.local_route && <p>{g.local_route}</p>}
    {g.official_starting_points?.length > 0 && (
      <ul className="space-y-1 text-sm" data-testid="ls-guidance-official">
        {g.official_starting_points.map((p) => <li key={p.url}><Ext href={p.url}>{p.label}</Ext></li>)}
      </ul>
    )}
    {g.check_in_your_district?.length > 0 && (<div><Sub>Check in your district</Sub><List items={g.check_in_your_district} /></div>)}
    {g.education && <p><span className="font-semibold">Education:</span> {g.education} {(g.education_source_ids || []).map((i) => reg[i] && <Ext key={i} href={reg[i].url}>{reg[i].title}</Ext>)}</p>}
    {g.family_conversation && <p><span className="font-semibold">Family conversation:</span> {g.family_conversation}</p>}
    {g.minors && <p><span className="font-semibold">Minors:</span> {g.minors}</p>}
    {g.dignity && <p><span className="font-semibold">Dignity:</span> {g.dignity}</p>}
  </div>
);

export const SharedGuidance = ({ guidance }) => {
  if (!guidance || !Object.keys(guidance).length) return null;
  const reg = Object.fromEntries((guidance.source_register || []).map((s) => [s.id, s]));
  const lj = guidance.learning_journey;
  const ai = guidance.ai_workforce_reality;
  const ind = guidance.india_guidance;
  const pr = guidance.practical_record;
  const ar = guidance.alternative_routes;
  const rm = guidance.review_method;
  const rn = guidance.release_notes;
  const links = (ar?.links || []).filter((l) => l.route);
  return (
    <Card id="shared_guidance" label="Shared guidance for every skill" Icon={BookMarked}
      cardCls="border-teal-200 bg-teal-50/40" iconCls="from-teal-400 to-emerald-500">
      <p className="text-sm text-gray-600">Applies to all skills. Facts, learning guidance and conditional automation scenarios are labelled separately.</p>
      {Array.isArray(lj) && (
        <Accordion title="Learning journey — six stages" testId="ls-guidance-journey">
          <ol className="space-y-2 text-[15px]">
            {lj.map((s) => (
              <li key={s.stage}><span className="font-bold text-gray-900">{s.stage}</span> — {s.action} <span className="text-sm text-gray-600">Evidence: {s.evidence}</span></li>
            ))}
          </ol>
        </Accordion>
      )}
      {ai && <Accordion title={ai.heading || 'AI workforce reality'} testId="ls-guidance-ai"><AiWorkforce a={ai} reg={reg} /></Accordion>}
      {ind && <Accordion title={ind.heading || 'India guidance'} testId="ls-guidance-india"><India g={ind} reg={reg} /></Accordion>}
      {pr && (
        <Accordion title="Your practical record — private learning notes" testId="ls-guidance-record">
          <div className="space-y-2 text-[15px]">
            <List items={pr.fields} />
            {pr.use && <p className="text-sm">{pr.use}</p>}
            {pr.assessment && <p className="text-sm text-gray-600">{pr.assessment}</p>}
          </div>
        </Accordion>
      )}
      {ar && links.length > 0 && (
        <Accordion title={ar.heading || 'Next steps'} testId="ls-guidance-routes">
          {ar.text && <p className="text-[15px] mb-3">{ar.text}</p>}
          <div className="flex flex-wrap gap-2" data-testid="ls-guidance-links">
            {links.map((l) => (
              <Link key={l.target} to={l.route} data-testid={`ls-route-${l.target}`}
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full border border-teal-300 bg-teal-50 text-teal-800 hover:bg-teal-100 transition-colors">
                {l.label} <ArrowRight size={14} />
              </Link>
            ))}
          </div>
        </Accordion>
      )}
      {guidance.source_register && (
        <Accordion title={`Sources (${guidance.source_register.length})`} testId="ls-guidance-sources">
          <ol className="space-y-1.5 text-sm" data-testid="ls-guidance-source-list">
            {guidance.source_register.map((s, i) => (
              <li key={s.id}>{i + 1}. <Ext href={s.url}>{s.title}</Ext>{s.publisher && <span className="text-gray-600"> — {s.publisher}</span>}{s.claim_scope && <span className="block text-xs text-gray-500">{s.claim_scope}</span>}</li>
            ))}
          </ol>
        </Accordion>
      )}
      {(rm || rn) && (
        <Accordion title="Content review and change notes" testId="ls-guidance-review">
          <div className="space-y-2 text-[15px]">
            {rm?.reviewed_on && <p><span className="font-semibold">Reviewed:</span> {rm.reviewed_on} — a review date is not individual verification.</p>}
            {rm?.basis && <p>{rm.basis}</p>}
            {rm?.limits?.length > 0 && (<div><Sub>Limits</Sub><List items={rm.limits} /></div>)}
            {rn?.changes?.length > 0 && (<div><Sub>Changes · {rn.date}</Sub><List items={rn.changes} /></div>)}
          </div>
        </Accordion>
      )}
    </Card>
  );
};
